import { useEffect, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import { validateCpf } from "@/app/utils/cpfFormater";
import { usePanMutations } from "@/app/hooks/usePanMutations";
import { customerService } from "@/app/services/customerService";

const getPersonalInfoFormSchema = z.object({
  cpf: z
    .string()
    .min(11, "CPF é obrigatório")
    .refine((v) => validateCpf(v), "CPF Inválido")
    .transform((f) => f.replace(/\D/g, "")),
  name: z.string().min(1, "Nome é obrigatório"),
  email: z.string().min(1, "E-mail é obrigatório"),
  phone: z.string().min(1, "Telefone é obrigatório"),
});

type getPersonalInfoFormSchemaData = z.infer<typeof getPersonalInfoFormSchema>;

interface usePersonalInfoFormModalProps {
  onContinue?: (data: getPersonalInfoFormSchemaData) => void;
  onSaveProposalData: (data: {
    propostaId: string;
    cpf: string;
    phone: string;
  }) => void;
}

export default function usePersonalInfoFormModal({
  onContinue,
  onSaveProposalData,
}: usePersonalInfoFormModalProps) {
  const getPersonalInfoForm = useForm<getPersonalInfoFormSchemaData>({
    resolver: zodResolver(getPersonalInfoFormSchema),
    mode: "onChange",
  });

  const { watch, setValue, handleSubmit } = getPersonalInfoForm;
  const { makeProposalMutation } = usePanMutations();

  const [isLoading, setIsLoading] = useState(false);

  const cpf = watch("cpf");
  const cpfClean = cpf?.replace(/\D/g, "");

  const { data: customer, isSuccess } = useCustomerCpf(cpfClean);

  useEffect(() => {
    if (isSuccess && customer) {
      setValue("name", customer.name);
      setValue("email", customer.email);
    }
  }, [isSuccess, customer, setValue]);

  const handleSendProposal = handleSubmit(async (formData) => {
    setIsLoading(true);

    try {
      const newCustomer = await customerService.createCustomer({
        name: formData.name,
        email: formData.email,
        cpf: formData.cpf,
        phone: formData.phone.replace(/\D/g, ""),
      });

      const customerId = newCustomer._id;

      const proposal = await makeProposalMutation.mutateAsync({
        customerId,
      });

      onSaveProposalData?.({
        propostaId: proposal._id,
        cpf: formData.cpf,
        phone: formData.phone.replace(/\D/g, ""),
      });

      onContinue?.(formData);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  });

  return {
    form: getPersonalInfoForm,
    handleSendProposal,
    isLoading,
  };
}
